import { useState, useEffect } from 'react';
import { Filter } from 'lucide-react';
import type { Agent, CallFilters, SalesPhase } from '../types';
import { api } from '../api/client';

interface FiltersProps {
  filters: CallFilters;
  onFiltersChange: (filters: CallFilters) => void;
}

const timeRangeOptions = [
  { value: 'today', label: 'Heute' },
  { value: '7days', label: 'Letzte 7 Tage' },
  { value: '30days', label: 'Letzte 30 Tage' },
  { value: 'month', label: 'Diesen Monat' },
  { value: 'year', label: 'Dieses Jahr' },
  { value: 'all', label: 'Alle' },
];

const phaseOptions: { value: SalesPhase | ''; label: string }[] = [
  { value: '', label: 'Alle Phasen' },
  { value: 'erstkontakt', label: 'Erstkontakt' },
  { value: 'qualifikation', label: 'Qualifikation' },
  { value: 'angebot', label: 'Angebot' },
  { value: 'verhandlung', label: 'Verhandlung' },
  { value: 'abschluss', label: 'Abschluss' },
  { value: 'nachbearbeitung', label: 'Nachbearbeitung' },
];

const scoreOptions = [
  { value: '', label: 'Alle Scores' },
  { value: 'hot', label: 'Heiss (>25)' },
  { value: 'warm', label: 'Warm (0-25)' },
  { value: 'cold', label: 'Kalt (<0)' },
];

function getDateRange(timeRange: string): { startDate?: string; endDate?: string } {
  const now = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());

  switch (timeRange) {
    case 'today':
      return {
        startDate: today.toISOString(),
        endDate: new Date(today.getTime() + 86400000).toISOString(),
      };
    case '7days':
      return {
        startDate: new Date(today.getTime() - 7 * 86400000).toISOString(),
        endDate: new Date(today.getTime() + 86400000).toISOString(),
      };
    case '30days':
      return {
        startDate: new Date(today.getTime() - 30 * 86400000).toISOString(),
        endDate: new Date(today.getTime() + 86400000).toISOString(),
      };
    case 'month':
      return {
        startDate: new Date(now.getFullYear(), now.getMonth(), 1).toISOString(),
        endDate: new Date(today.getTime() + 86400000).toISOString(),
      };
    case 'year':
      return {
        startDate: new Date(now.getFullYear(), 0, 1).toISOString(),
        endDate: new Date(today.getTime() + 86400000).toISOString(),
      };
    default:
      return {};
  }
}

export default function Filters({ filters, onFiltersChange }: FiltersProps) {
  const [agents, setAgents] = useState<Agent[]>([]);
  const [timeRange, setTimeRange] = useState('month');

  useEffect(() => {
    const loadAgents = async () => {
      try {
        const data = await api.getAgents();
        setAgents(data);
      } catch (err) {
        console.error('Failed to load agents:', err);
      }
    };
    loadAgents();
  }, []);

  const handleTimeRangeChange = (value: string) => {
    setTimeRange(value);
    const dateRange = getDateRange(value);
    onFiltersChange({ ...filters, ...dateRange });
  };

  const handleAgentChange = (value: string) => {
    onFiltersChange({
      ...filters,
      userId: value || undefined,
    });
  };

  const handlePhaseChange = (value: string) => {
    onFiltersChange({
      ...filters,
      phase: (value as SalesPhase) || undefined,
    });
  };

  const handleScoreChange = (value: string) => {
    onFiltersChange({
      ...filters,
      leadScore: (value as 'hot' | 'warm' | 'cold') || undefined,
    });
  };

  return (
    <div className="bg-white border-b border-gray-200 px-6 py-4">
      <div className="flex items-center space-x-4">
        <Filter className="w-5 h-5 text-gray-400" />

        {/* User filter */}
        <select
          value={filters.userId || ''}
          onChange={(e) => handleAgentChange(e.target.value)}
          className="select w-48"
        >
          <option value="">Alle Benutzer</option>
          {agents.map((agent) => (
            <option key={agent.agent_id} value={agent.agent_id}>
              {agent.agent_name || agent.agent_email}
            </option>
          ))}
        </select>

        {/* Time range filter */}
        <select
          value={timeRange}
          onChange={(e) => handleTimeRangeChange(e.target.value)}
          className="select w-48"
        >
          {timeRangeOptions.map((option) => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
        </select>

        {/* Phase filter */}
        <select
          value={filters.phase || ''}
          onChange={(e) => handlePhaseChange(e.target.value)}
          className="select w-48"
        >
          {phaseOptions.map((option) => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
        </select>

        {/* Score filter */}
        <select
          value={filters.leadScore || ''}
          onChange={(e) => handleScoreChange(e.target.value)}
          className="select w-48"
        >
          {scoreOptions.map((option) => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
        </select>
      </div>
    </div>
  );
}
